import React from "react";
import { Box, Link, Typography, ListItem, ListItemText } from "@mui/material";
import {
  formatDateMonthDateYear,
  getPatientRoute,
  handleExternalLinkClick,
} from "../../utility/functions/common-functions";
import { useTranslation } from "react-i18next";
import styles from "./PointsEarnedSection.module.scss";
import { ActivityData } from "../../utility/interfaces/carePlanInterfaces";
import {
  CONTENT_STATUS_COMPLETED_ID,
  SAVE_CLICK_STATUS_ID,
} from "../../utility/constants/common-constants";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useSaveContentClickMutation } from "../view-learning-content/services/SaveContentStatusAccessLogAPISlice";
import { useSaveContentStatusMutation } from "../view-learning-content/services/SaveContentStatusAPISlice";
import { useLocation, useNavigate } from "react-router-dom";
import { setLearningContent } from "../view-learning-content/services/LearningContentDataSlice";

interface ActivityListItemProps {
  activity: ActivityData;
}

const ActivityListItem: React.FC<ActivityListItemProps> = ({ activity }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isVideo = activity.contentTypeCode === "videos";
  const isArticle = activity.contentTypeCode === "articles";

  const {
    preferredLanguage: lang,
    patientProfileId,
    userName: patientUserName,
  } = useAppSelector((state) => state.patientProfileDetails.patientProfileInfo);
  const [saveContentClick] = useSaveContentClickMutation();
  const [contentStatus] = useSaveContentStatusMutation();

  const showContetntDetails = async (content: ActivityData) => {
    // Handle external link content as before
    if (content?.contentTypeCode === "URL") {
      if (content?.milestoneLearningUrls) {
        window.open(
          content?.milestoneLearningUrls,
          "_blank",
          "noopener,noreferrer"
        );
        if (content?.activityCompletionStatus !== t("completed")) {
          handleExternalLinkClick(
            content.casePlanMilestoneMappingId,
            CONTENT_STATUS_COMPLETED_ID,
            patientUserName || "Patient",
            patientProfileId,
            lang,
            dispatch,
            contentStatus
          );
        }
      }
    } else {
      // Dispatch content to Redux
      dispatch(
        setLearningContent({
          type: content?.contentTypeCode,
          category: content?.activitySubCategoryName,
          videoUrl: content?.learningMetaData?.videoUrl,
          fullHtml: content?.learningMetaData?.fullHtml,
          title: content?.activityDisplayName,
          summarry: content?.learningMetaData?.summary,
          casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId,
          activityCompletionStatus: content?.activityCompletionStatus,
          patientProfileId,
          lang: lang,
          activityPoints: content?.activityPoints,
          thumbnail: content?.learningMetaData?.thumbnail,
          length: content?.learningMetaData?.length,
          educationalContentSourceId: content?.educationalContentSourceId,
          educationalContentIdentifier: content?.contentCode.toString(),
          navigationInfo: {
            isViewLearningContentEnabled: true,
            arrivedFrom: location.pathname
          }
        })
      );

      // Navigate to the learning view
      navigate(getPatientRoute(patientProfileId, "/learning"), {
        state: {
          selectedTabId: 5,
        },
      });
    }

    // Save click mutation
    try {
      const body = {
        casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId || "",
        patientProfileId: patientProfileId,
        planId: 1,
        caseId: 1,
        milestoneId: 1,
        contentIdentifier: content?.contentCode.toString(),
        contentWatchedDurationInMins: 1,
        action: SAVE_CLICK_STATUS_ID,
        educationalContentSourceId: content?.educationalContentSourceId,
      };

      await saveContentClick({
        body,
        lang,
      }).unwrap();
    } catch (err: any) {
      console.log(err, "saveContentClick");
    }
  };

  return (
    <ListItem
      disableGutters
      sx={{ justifyContent: "space-between", py: 2 }}
      className={styles.listItem}
    >
      <Box>
        <Typography variant="body2">
          {(isVideo || isArticle) && (
            <Box component="span" className={styles.linkText} mr={1}>
              <Link onClick={() => showContetntDetails(activity)}>
                {isVideo ? t("carePlan.watch") : t("carePlan.read")}
              </Link>
            </Box>
          )}
          {activity.milestoneLearningUrls ? (
            <Link onClick={() => showContetntDetails(activity)}>
              {activity.activityDisplayName}
            </Link>
          ) : (
            activity.activityDisplayName
          )}
        </Typography>
        <Typography variant="bodySmall" color="text.secondary">
          {t("myRewardsPage.completedOn")}{" "}
          {formatDateMonthDateYear(activity.updatedDateTime)}
        </Typography>
      </Box>
      <ListItemText
        primary={<Typography>{activity.activityPoints}</Typography>}
        sx={{ textAlign: "right", flex: "0 0 auto", pl: 2 }}
      />
    </ListItem>
  );
};

export default ActivityListItem;
